/**
 * DocsRenderer - Rendering logic for DocsApp_v2
 * 
 * Renders nodes to DOM elements with proper styling and behavior
 */

import { getClassInfo } from './DocsConfig';

export class DocsRenderer {
  /**
   * Render a node to HTMLElement
   */
  render(node: any): HTMLElement {
    const className = node.className || node.getAttribute?.('className');
    
    if (!className) {
      console.warn('[DocsRenderer] Node has no className, rendering as div');
      return this.renderFallback(node);
    }
    
    // Get class info from config
    const classInfo = getClassInfo(className);
    
    if (classInfo && classInfo.render) {
      try {
        return classInfo.render(node);
      } catch (error) {
        console.error(`[DocsRenderer] Error rendering ${className}:`, error);
        return this.renderError(node, error);
      }
    }
    
    // Fallback rendering
    console.warn(`[DocsRenderer] No renderer for className: ${className}`);
    return this.renderFallback(node);
  }
  
  /**
   * Render fallback (when no specific renderer)
   */
  private renderFallback(node: any): HTMLElement {
    const el = document.createElement('div');
    el.className = 'unstyled-node';
    el.textContent = node.text || node.textContent || '[No content]';
    el.style.padding = '8px';
    el.style.border = '1px dashed #ccc';
    el.style.margin = '4px 0';
    return el;
  }
  
  /**
   * Render error state
   */
  private renderError(node: any, error: any): HTMLElement {
    const el = document.createElement('div');
    el.className = 'render-error';
    el.style.padding = '12px';
    el.style.background = '#fee';
    el.style.border = '1px solid #f88';
    el.style.borderRadius = '4px';
    el.style.margin = '8px 0';
    
    const title = document.createElement('strong');
    title.textContent = 'Render Error';
    title.style.color = '#c00';
    
    const message = document.createElement('p');
    message.textContent = error.message || 'Unknown error';
    message.style.margin = '4px 0 0 0';
    message.style.fontSize = '0.9em';
    
    el.appendChild(title);
    el.appendChild(message);
    
    return el;
  }
  
  /**
   * Render multiple nodes
   */
  renderAll(nodes: any[]): HTMLElement[] {
    return nodes.map(node => this.render(node));
  }
  
  /**
   * Render nodes into a container
   */
  renderIntoContainer(nodes: any[], container: HTMLElement): void {
    // Clear container
    container.innerHTML = '';
    
    // Render and append each node
    for (const node of nodes) {
      const el = this.render(node);
      container.appendChild(el);
    }
  }
  
  /**
   * Enhanced rendering with additional features
   * (line numbers, copy button, etc.)
   */
  renderEnhanced(node: any): HTMLElement {
    const className = node.className || node.getAttribute?.('className');
    const baseEl = this.render(node);
    
    // Add enhancements based on className
    if (className === 'code-block') {
      return this.enhanceCodeBlock(baseEl, node);
    } else if (className?.startsWith('callout-')) {
      return this.enhanceCallout(baseEl, node);
    }
    
    return baseEl;
  }
  
  /**
   * Enhance code block with copy button
   */
  private enhanceCodeBlock(el: HTMLElement, node: any): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'code-block-wrapper';
    wrapper.style.position = 'relative';
    
    // Add copy button
    const copyBtn = document.createElement('button');
    copyBtn.textContent = 'Copy';
    copyBtn.className = 'code-copy-btn';
    copyBtn.style.position = 'absolute';
    copyBtn.style.top = '8px';
    copyBtn.style.right = '8px';
    copyBtn.style.padding = '4px 8px';
    copyBtn.style.fontSize = '0.8em';
    copyBtn.style.cursor = 'pointer';
    
    copyBtn.addEventListener('click', () => {
      const code = node.text || node.textContent || '';
      navigator.clipboard.writeText(code).then(() => {
        copyBtn.textContent = 'Copied!';
        setTimeout(() => {
          copyBtn.textContent = 'Copy';
        }, 2000);
      });
    });
    
    wrapper.appendChild(el);
    wrapper.appendChild(copyBtn);
    
    return wrapper;
  }
  
  /**
   * Enhance callout with icon
   */
  private enhanceCallout(el: HTMLElement, node: any): HTMLElement {
    const className = node.className || node.getAttribute?.('className');
    
    // Add icon based on type
    const iconMap: Record<string, string> = {
      'callout-info': 'ℹ️',
      'callout-warning': '⚠️',
      'callout-success': '✅',
      'callout-error': '❌'
    };
    
    const icon = iconMap[className] || '📌';
    
    const iconEl = document.createElement('span');
    iconEl.textContent = icon;
    iconEl.className = 'callout-icon';
    iconEl.style.marginRight = '8px';
    iconEl.style.fontSize = '1.2em';
    
    el.insertBefore(iconEl, el.firstChild);
    
    return el;
  }
  
  /**
   * Render with ProseMirror compatibility
   * Converts node to ProseMirror-compatible structure
   */
  renderProseMirrorNode(node: any): any {
    const className = node.className || node.getAttribute?.('className');
    
    // Map className to ProseMirror node type
    // For now, simplified mapping
    
    return {
      type: this.mapClassNameToNodeType(className),
      attrs: node.attrs || {},
      content: node.content || []
    };
  }
  
  /**
   * Map className to ProseMirror node type
   */
  private mapClassNameToNodeType(className: string): string {
    // For universal schema, we use generic types
    // className drives rendering, not node type
    
    if (className?.startsWith('heading-')) {
      return 'heading';
    } else if (className === 'code-block') {
      return 'code_block';
    } else if (className === 'image') {
      return 'image';
    } else if (className === 'bullet-list' || className === 'ordered-list') {
      return 'list';
    } else if (className === 'list-item') {
      return 'list_item';
    } else {
      return 'paragraph';
    }
  }
  
  /**
   * Create interactive elements (checkboxes, etc.)
   */
  private createInteractiveElement(node: any): HTMLElement {
    const className = node.className || node.getAttribute?.('className');
    
    if (className === 'checkbox') {
      const label = document.createElement('label');
      label.style.display = 'flex';
      label.style.alignItems = 'center';
      label.style.cursor = 'pointer';
      
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = node.attrs?.checked || false;
      checkbox.style.marginRight = '8px';
      
      const text = document.createElement('span');
      text.textContent = node.text || '';
      
      label.appendChild(checkbox);
      label.appendChild(text);
      
      return label;
    }
    
    return this.render(node);
  }
}

